import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { formatRewardAmount } from "@/lib/textUtils";
import { type AdminStats } from "@/lib/schema";
import { useQuery } from "@tanstack/react-query";
import { getAllRewards } from "@/api/stats";
import { RewardStatus } from "@/lib/schema";

// Import dashboard icons for consistency
import iconRewardsEarned from "@/assets/dashboard/icon_rewardsearned.svg";
import iconPaid from "@/assets/rewards/icon_paid.svg";

interface RewardsCardData {
  title: string;
  value: number;
  icon: string;
  variant?: "default" | "dark";
}

interface RewardsSectionProps {
  adminStats: AdminStats | undefined;
  loading?: boolean;
}

const RewardsCardSkeleton = () => (
  <Card className="border-0 shadow-sm flex-1">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <Skeleton className="h-8 w-16 mb-2" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="w-10 h-10 rounded-lg" />
      </div>
    </CardContent>
  </Card>
);

const RewardsCard = ({ title, value, icon, variant = "default" }: RewardsCardData) => {
  const isDark = variant === "dark";
  
  return (
    <Card 
      className={`border-0 shadow-sm ${isDark ? "text-white" : ""} flex-1`}
      style={isDark ? { backgroundColor: '#2D2D2D' } : {}}
      data-testid={`rewards-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent 
        className="p-0"
        style={{
          display: 'flex',
          padding: '16px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px'
        }}
      >
        <img 
          src={icon} 
          alt={title}
          className="h-6 w-6 sm:h-7 sm:w-7"
        />
        <p className={`text-2xl sm:text-3xl font-bold space-grotesk ${
          isDark ? "text-white" : "text-gray-900"
        }`}>
          R{formatRewardAmount(value)}
        </p>
        <p className={`text-xs font-medium uppercase space-grotesk leading-tight tracking-[0.07px] ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}>
          {title}
        </p>
      </CardContent>
    </Card>
  );
};

export const RewardsSection = ({ adminStats, loading }: RewardsSectionProps) => {
  // Fetch actual rewards data
  const { data: rewards = [], isLoading: rewardsLoading } = useQuery({
    queryKey: ["admin-rewards-section"],
    queryFn: getAllRewards,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  // Calculate totals from actual rewards data
  const totalRewardsDistributed = rewards.reduce((sum, reward) => {
    return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
  }, 0);

  const paidRewards = rewards
    .filter(reward => reward.status === RewardStatus.PAID)
    .reduce((sum, reward) => {
      return sum + parseInt(reward.amount.replace(/[$,]/g, ''));
    }, 0);

  const rewardsData: RewardsCardData[] = [
    {
      title: "Rewards Released",
      value: paidRewards,
      icon: iconPaid,
      variant: "default" as const
    },
    {
      title: "Total Rewards Accumulated", 
      value: totalRewardsDistributed,
      icon: iconRewardsEarned,
      variant: "default" as const
    }
  ];

  const isLoading = loading || rewardsLoading;

  return (
    <div className="lg:col-span-1 flex flex-col gap-6">
      {isLoading ? (
        <>
          <RewardsCardSkeleton />
          <RewardsCardSkeleton />
        </>
      ) : (
        rewardsData.map((reward, index) => (
          <RewardsCard
            key={index}
            title={reward.title}
            value={reward.value}
            icon={reward.icon}
            variant={reward.variant}
          />
        ))
      )}
    </div>
  );
};
